/**
 * Working out a quantity for a line added by hand.
 *
 * The generator does this from a template, where the section a product sits in
 * decides which measurement drives it. A line added by hand to an order has no
 * section — but the product still knows what it is measured against, and the
 * job still has a roof. So the same arithmetic can be reached from the other
 * direction: read the product's own measurement key, find that number on the
 * roof, and divide.
 *
 * It is a suggestion, not a rule. Whatever comes back lands in an editable
 * quantity box, and a person who meant to type 2 types 2. Returning null when
 * the answer is not knowable is therefore better than returning 1 — one is a
 * number somebody might not look at twice.
 */

import {
  asLayouts,
  quantityFor,
  type GeneratorProduct,
  type RoofLayouts,
  type RoofMeasurements,
} from "./generator.js";

/**
 * A downspout every 20 feet of eave, and 15 feet of pipe in each one.
 *
 * Jonathon, 2026-08-15: "for gutters to calculate and include the downspouts
 * lf, divide eaves by 20. then multiply that number by 15. because you need a
 * downspout for every 20lf of eave and on average downspouts are 15' long."
 *
 * Averages, deliberately — the downspout count is **not** rounded up. Both
 * numbers are already an average standing in for a house nobody has counted
 * the corners of, and rounding a 6.5 up to 7 would add a downspout's worth of
 * pipe to every job on the strength of an estimate.
 */
export const DOWNSPOUT_EVERY_FEET = 20;
export const DOWNSPOUT_LENGTH_FEET = 15;

/**
 * The extra 10% on the eave, for gutters and for gutter guards.
 *
 * Jonathon, same conversation: "add 10% to eave measurement for gutters and
 * gutter guards."
 *
 * It lives here rather than in the products' waste column, which is where it
 * belongs by rights, for two reasons — see ALLOWANCE_INCLUDED below for the
 * first, and for the second: gutters measure eave *plus* downspouts, which a
 * percentage cannot say, and applying one to the sum would put waste on
 * footage the 15-foot average already stands in for.
 */
export const GUTTER_EAVE_ALLOWANCE_PCT = 10;

/**
 * Gutter footage for a roof: eave plus its allowance, plus the downspouts.
 *
 * Exported because it is the owner's formula rather than an implementation
 * detail, and because the number it produces is on a quote.
 */
export function gutterRunLength(eaveLf: number): number {
  const eave = num(eaveLf);
  if (eave <= 0) return 0;
  const downspouts = (eave / DOWNSPOUT_EVERY_FEET) * DOWNSPOUT_LENGTH_FEET;
  return feet(eaveWithAllowance(eave) + downspouts);
}

/** The eave with the owner's 10% on it — what a gutter or a guard runs. */
export function eaveWithAllowance(eaveLf: number): number {
  return feet(num(eaveLf) * (1 + GUTTER_EAVE_ALLOWANCE_PCT / 100));
}

/**
 * Feet, to the hundredth.
 *
 * Not fussiness: `180 * 1.1` is 198.00000000000003 in binary floating point,
 * and the quantity that comes out of here is rounded **up** to whole units. An
 * exact 198 feet of gutter would have been ordered and quoted as 199.
 */
const feet = (value: number): number => Math.round(value * 100) / 100;

/**
 * Keys that already contain their own allowance, so no waste is added on top.
 *
 * Both gutter keys work the eave out to the foot the owner asked for. Waste
 * would otherwise land on them twice over, and from the wrong source: a roof
 * carrying its own `waste_pct` beats the product's everywhere else (see
 * below), so a 15%-waste roof would quietly turn "eave plus 10%" into "eave
 * plus 15%" and no screen would say why.
 */
const ALLOWANCE_INCLUDED = new Set([
  "GUTTER_RUN_LENGTH",
  "GUTTER_GUARD_LENGTH",
]);

/** Roofr's measurement keys, mapped onto the roof we hold. */
const KEYS: Record<string, (m: RoofMeasurements) => number> = {
  // Gutters and guards only. `EAVES_LENGTH` stays exactly what it says for the
  // twenty-odd other products keyed to it — drip edge wants the eave, not the
  // eave plus a gutter allowance, and certainly not the downspouts.
  GUTTER_RUN_LENGTH: (m) => gutterRunLength(num(m.eave_lf)),
  GUTTER_GUARD_LENGTH: (m) => eaveWithAllowance(num(m.eave_lf)),
  TOTAL_PITCH_AREA: (m) => num(m.squares) * 100,
  TOTAL_PITCH_AREA_SQUARES: (m) => num(m.squares),
  TOTAL_FLAT_AREA: (m) => num(m.flat_squares) * 100,
  TOTAL_FLAT_AREA_SQUARES: (m) => num(m.flat_squares),
  EAVES_LENGTH: (m) => num(m.eave_lf),
  RAKES_LENGTH: (m) => num(m.rake_lf),
  RIDGES_LENGTH: (m) => num(m.ridge_lf),
  HIPS_LENGTH: (m) => num(m.hip_lf),
  VALLEYS_LENGTH: (m) => num(m.valley_lf),
  GUTTERS_LENGTH: (m) => num(m.gutter_lf),
  WALL_FLASHING_LENGTH: (m) => num(m.flashing_lf),
  STEP_FLASHING_LENGTH: (m) => num(m.step_flashing_lf),
  TRANSITIONS_LENGTH: (m) => num(m.transition_lf),
};

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export interface MeasuredQuantity {
  qty: number;
  /** What it was worked out from — "Eave + Rake = 300 LF". */
  basis: string;
}

/**
 * The quantity a product needs for this roof, or null if it cannot be known.
 *
 * Null means one of three things, and all of them are reasons to leave the box
 * alone rather than fill it: the product has no coverage, it is not measured
 * against anything, or this roof has no such measurement. A product listing
 * several keys is summed — drip edge keyed to eaves and rakes wants both.
 *
 * Every roof on the job counts. Pass them all and the suggestion covers the
 * house and the detached garage together, the same as the generator does.
 */
export function measuredQuantity(
  product: Pick<GeneratorProduct, "coverage" | "waste" | "measurement">,
  measurements: RoofLayouts | null | undefined,
  layoutWaste?: unknown,
): MeasuredQuantity | null {
  if (!measurements) return null;
  const layouts = asLayouts(measurements);
  if (layouts.length === 0) return null;

  const keys = (product.measurement ?? "")
    .split(",")
    .map((k) => k.trim().toUpperCase())
    .filter((k) => k in KEYS);
  if (keys.length === 0) return null;

  // A product keyed to both the raw area and the squares variant is counted in
  // squares — the same rule the generator applies, for the same reason.
  const wanted = keys.some((k) => k.endsWith("_SQUARES"))
    ? keys.filter((k) => k.endsWith("_SQUARES"))
    : keys;

  let measured = 0;
  const parts: string[] = [];
  for (const key of wanted) {
    const value = layouts.reduce((total, roof) => total + KEYS[key]!(roof), 0);
    if (value <= 0) continue;
    measured += value;
    parts.push(label(key));
  }
  if (measured <= 0) return null;

  // The same rule the generator uses: the biggest allowance any roof carries.
  // Except where the measurement already carries one of its own — see
  // ALLOWANCE_INCLUDED.
  const stated = layouts
    .filter((roof) => roof.waste_pct != null)
    .map((roof) => num(roof.waste_pct));
  const waste = wanted.every((key) => ALLOWANCE_INCLUDED.has(key))
    ? 0
    : (layoutWaste ??
      (stated.length > 0 ? Math.max(...stated) : undefined) ??
      product.waste);
  const qty = quantityFor(measured, product.coverage, num(waste));
  if (qty === null || qty <= 0) return null;

  return {
    qty,
    basis: `${parts.join(" + ")} = ${trim(measured)} ${unitOf(wanted[0]!)}`,
  };
}

const label = (key: string): string =>
  ({
    TOTAL_PITCH_AREA: "Field",
    TOTAL_PITCH_AREA_SQUARES: "Field",
    TOTAL_FLAT_AREA: "Flat",
    TOTAL_FLAT_AREA_SQUARES: "Flat",
    EAVES_LENGTH: "Eave",
    RAKES_LENGTH: "Rake",
    RIDGES_LENGTH: "Ridge",
    HIPS_LENGTH: "Hip",
    VALLEYS_LENGTH: "Valley",
    GUTTERS_LENGTH: "Gutter",
    GUTTER_RUN_LENGTH: "Eave +10% + downspouts",
    GUTTER_GUARD_LENGTH: "Eave +10%",
    WALL_FLASHING_LENGTH: "Wall flashing",
    STEP_FLASHING_LENGTH: "Step flashing",
    TRANSITIONS_LENGTH: "Transition",
  })[key] ?? key;

const unitOf = (key: string): string =>
  key === "TOTAL_PITCH_AREA" || key === "TOTAL_FLAT_AREA"
    ? "SF"
    : key.endsWith("_SQUARES")
      ? "SQ"
      : "LF";

const trim = (n: number): string =>
  Number(n.toFixed(2)).toLocaleString("en-US");
